/**
 * EventEditSheet (Plan 03-08).
 *
 * Full event create/edit form per UI-SPEC §6 EventEditSheet:
 *   - Phone: mounts inside BottomSheet (slide-up)
 *   - Tablet (≥1024px): centred modal (dialog-like div with scrim)
 *
 * Field stack (top to bottom, #157 steg 7b):
 *   1. Topphode: X (Avbryt) · "Ny hendelse" / "Rediger hendelse" · Lagre
 *   2. "Hva skjer?" (required)
 *   3. "Hele dagen" (Switch)
 *   4. Start / Slutt (or Dato when all-day)
 *   5. Påminnelser: ReminderPresetChips
 *   6. Gjentas: RecurrenceBuilderField
 *   7. Override scope picker (only for recurring occurrence edits)
 *   8. "Lagt til av {nick}" caption
 *   9. Slett (gated)
 *
 * D-08: Slett visible only if event.created_by === viewer.id || viewer.role === 'admin'.
 * D-16: offline check before submit; toast shown, sheet stays open, no queue.
 * T-CAL-37: UI gate + API enforces independently (defense in depth).
 * T-CAL-38: title/description are React children (auto-escaped).
 * T-CAL-39: RecurrenceBuilderField uses RecurrenceBuilder Zod validation.
 *
 * Utseende (#157, steg 7b, Stell_Hendelse-skissen). Arket forblir et høyt ark
 * (D9), og skissens topp — X, tittel og Lagre — er arkets topphode. Det står
 * fast øverst når arket rulles, så Lagre alltid kan nås.
 *   - X er Avbryt (`cancel-btn`); den gamle knapperaden nederst er borte.
 *     Slett står alene nederst for den som har lov (D-08).
 *   - Feltene: «Hva skjer?», «Hele dagen» som glassrad med Switch, Start og
 *     Slutt, Påminnelser som chips og «Gjentas»-raden.
 *   - Sted og «Hvem skal være med?» er ny oppførsel 5 og er ikke bygget.
 *     Sted og deltakerne bevares fortsatt uendret ved redigering.
 * Datoene, Oslo-tiden, påminnelsene og gjentakelsen regnes som før.
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, SheetError, SheetHeader, Switch } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { RecurrenceBuilderField } from './RecurrenceBuilderField';
import { RecurrenceOverrideScopePicker, type Scope } from './RecurrenceOverrideScopePicker';
import { ReminderPresetChips, type ReminderPreset, PRESET_LEAD_MINUTES } from './ReminderPresetChips';
import { DeleteEventConfirmSheet } from './DeleteEventConfirmSheet';
import { useEventMutations, type EventInput } from './useEventMutations';
import type { RecurrenceBuilder } from '@stell/shared';

// ─── mode types ─────────────────────────────────────────────────────────────

export type EventEditSheetMode =
  | {
      kind: 'create';
      defaults?: Partial<{
        starts_at: string;
        attendee_ids: string[];
      }>;
    }
  | {
      kind: 'edit';
      eventId: string;
      seriesId: string;
      isOccurrence: boolean;
      occurrenceStart?: string;
      initial: {
        title: string;
        description?: string | null;
        starts_at: string;
        ends_at?: string | null;
        all_day: boolean;
        location?: string | null;
        color_token?: string | null;
        recurrence?: RecurrenceBuilder;
        attendee_ids: string[];
        reminders?: Array<{ member_id: string; lead_minutes: number }>;
      };
      createdBy: string;
      /** Navnet til «Lagt til av» (#165). Uten navn skjules linja. */
      createdByName?: string;
      viewerId: string;
      viewerRole: 'admin' | 'teen' | 'child';
    };

export interface EventEditSheetProps {
  open: boolean;
  onClose: () => void;
  mode: EventEditSheetMode;
}

// ─── session hook (same pattern as ApprovalsRoute) ──────────────────────────

interface SessionMember {
  id: string;
  role: 'admin' | 'teen' | 'child';
  short_name: string;
}

function useSession(): { viewer: SessionMember | null; loaded: boolean } {
  const [viewer, setViewer] = useState<SessionMember | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', { credentials: 'same-origin' });
        if (!r.ok) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const body = (await r.json()) as { member?: SessionMember };
        if (!cancelled) {
          setViewer(body.member ?? null);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { viewer, loaded };
}

// ─── media query hook (inline — no external dep) ────────────────────────────

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

// ─── lead_minutes resolver for reminder presets ──────────────────────────────

/**
 * Resolve a ReminderPreset[] + event starts_at to concrete lead_minutes values.
 *
 * For oneHour: 60 min (fixed).
 * For eveningBefore: compute from event time back to 20:00 prev day.
 *   Example: event at 16:30 → fire at 20:00 prev day → (24h - 16:30 + 20:00 = ~27.5h?) No:
 *   lead = (event_local_day - 1) at 20:00 → event at 16:30 → prev 20:00 is 20.5h before = 1230 min
 * For morningOf: compute from event time back to 07:00 same day.
 *   Example: event at 16:30 → fire at 07:00 same day → 9.5h = 570 min
 * These are approximations for the first occurrence; server materialises per actual occurrence.
 */
function resolveLeadMinutes(preset: Exclude<ReminderPreset, 'none'>, startsAt: string): number {
  if (preset === 'oneHour') return 60;
  const eventDate = new Date(startsAt);
  const localMinutes = eventDate.getHours() * 60 + eventDate.getMinutes();

  if (preset === 'eveningBefore') {
    // Previous day at 20:00 = 20*60 = 1200 min from midnight
    // Lead = (current day local minutes + (24*60 - 1200)) = local_minutes + 240 (mins after prev day 20:00)
    // Simpler: lead = local_minutes + 240 (time from prev 20:00 to event)
    return localMinutes + (24 * 60 - 20 * 60);
  }
  if (preset === 'morningOf') {
    // Same day at 07:00 = 7*60 = 420 min from midnight
    // Lead = local_minutes - 420
    const lead = localMinutes - 7 * 60;
    return lead > 0 ? lead : 60; // fallback to 1h if event is before 07:00
  }
  return PRESET_LEAD_MINUTES[preset];
}

/** Feilteksten under et felt (tittel, slutt). */
const FIELD_ERROR = 'm-0 mt-1.5 text-caption font-semibold text-bad';

// ─── next round 15-min boundary helper ──────────────────────────────────────

function nextRound15(): string {
  const now = new Date();
  const mins = now.getMinutes();
  const roundedMins = Math.ceil(mins / 15) * 15;
  const next = new Date(now);
  next.setMinutes(roundedMins, 0, 0);
  // ISO local format for <input type="datetime-local">
  return formatDatetimeLocal(next);
}

function formatDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── main component ──────────────────────────────────────────────────────────

export function EventEditSheet({ open, onClose, mode }: EventEditSheetProps) {
  const { t } = useTranslation();
  const isTablet = useMediaQuery('(min-width: 1024px)');
  const { viewer } = useSession();
  const { create, update, remove } = useEventMutations();

  // ── form state ──
  const defaultStartsAt = mode.kind === 'create'
    ? (mode.defaults?.starts_at ?? nextRound15())
    : mode.initial.starts_at.slice(0, 16).replace('T', 'T');

  const defaultEndsAt = mode.kind === 'create'
    ? (() => {
        const d = new Date(defaultStartsAt);
        d.setHours(d.getHours() + 1);
        return formatDatetimeLocal(d);
      })()
    : (mode.initial.ends_at ? mode.initial.ends_at.slice(0, 16) : '');

  const [title, setTitle] = useState(
    mode.kind === 'edit' ? mode.initial.title : '',
  );
  const [allDay, setAllDay] = useState(
    mode.kind === 'edit' ? mode.initial.all_day : false,
  );
  const [startsAt, setStartsAt] = useState(defaultStartsAt);
  const [endsAt, setEndsAt] = useState(defaultEndsAt);
  const location = mode.kind === 'edit' ? (mode.initial.location ?? '') : '';
  const description = mode.kind === 'edit' ? (mode.initial.description ?? '') : '';
  const [attendeeIds, setAttendeeIds] = useState<string[]>(
    mode.kind === 'edit'
      ? mode.initial.attendee_ids
      : (mode.defaults?.attendee_ids ?? (viewer ? [viewer.id] : [])),
  );
  const [recurrence, setRecurrence] = useState<RecurrenceBuilder>(
    mode.kind === 'edit' && mode.initial.recurrence
      ? mode.initial.recurrence
      : { type: 'once' },
  );
  const [reminderPresets, setReminderPresets] = useState<ReminderPreset[]>(['none']);
  const [scope, setScope] = useState<Scope>('single');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ── validation errors ──
  const [errors, setErrors] = useState<{
    title?: string;
    ends?: string;
    submit?: string;
  }>({});

  // ── permission: can delete? ──
  // En ukjent seer (økta ikke lastet) er aldri skaperen, selv om createdBy også er tom.
  const canDelete: boolean =
    mode.kind === 'edit' &&
    ((mode.viewerId !== '' && mode.viewerId === mode.createdBy) || mode.viewerRole === 'admin');

  // ── is editing recurring occurrence? ──
  const isRecurringOccurrence =
    mode.kind === 'edit' && mode.isOccurrence && !!(mode.initial.recurrence);

  // ── engangshendelse? ──
  // Bare en engangshendelse har samme id som serien sin; en forekomst er
  // `serie|tid`. Den oppdateres og slettes som helhet (scope=series). Alt annet
  // er en forekomst og går til serien med omfang og forekomst — uten
  // omfangsvelger er omfanget «bare denne», så serien aldri endres ved et uhell.
  const isOneOff =
    mode.kind === 'edit' && !mode.isOccurrence && mode.eventId === mode.seriesId;

  // Forekomstens nøkkel i serien er tiden etter «|» i id-en: regelens tid.
  // occurrence_start er den flyttede tiden for en flyttet forekomst (og for
  // heldag, siden lagret tid og nøkkel er ulike), så den treffer ikke overstyringen.
  const occurrence =
    mode.kind === 'edit' && !isOneOff
      ? (mode.eventId.split('|')[1] ?? mode.occurrenceStart)
      : undefined;

  // ── reset form on open ──
  useEffect(() => {
    if (!open) return;
    setErrors({});
    // Re-initialize state if mode changes
    setTitle(mode.kind === 'edit' ? mode.initial.title : '');
    setAllDay(mode.kind === 'edit' ? mode.initial.all_day : false);
    setRecurrence(
      mode.kind === 'edit' && mode.initial.recurrence
        ? mode.initial.recurrence
        : { type: 'once' },
    );
    setScope('single');
    setShowDeleteConfirm(false);
    // Bevisst bare [open]: skjemaet nullstilles når arket åpnes, ikke ved hver ny mode.
  }, [open]);

  // ── seed viewer as default attendee once session loads (create mode only) ──
  // The attendeeIds state is initialized before useSession resolves, so viewer
  // may be null at mount time. Once viewer is available and no defaults were
  // provided, add the viewer as the first attendee.
  useEffect(() => {
    if (!open) return;
    if (mode.kind !== 'create') return;
    if (mode.defaults?.attendee_ids) return; // explicit defaults provided
    if (!viewer) return;
    setAttendeeIds((prev) => (prev.length === 0 ? [viewer.id] : prev));
  // viewer.id is stable once loaded; open/mode.kind guard prevents stale runs
  }, [open, viewer?.id]);

  // ── validation ──
  const validate = useCallback((): boolean => {
    const errs: typeof errors = {};
    if (!title.trim()) {
      errs.title = t('calendar.error.titleRequired');
    }
    if (!allDay && endsAt && startsAt && endsAt < startsAt) {
      errs.ends = t('calendar.error.endBeforeStart');
    }
    if (!allDay && startsAt && endsAt) {
      const start = new Date(startsAt);
      const end = new Date(endsAt);
      if (
        end.getDate() !== start.getDate() ||
        end.getMonth() !== start.getMonth() ||
        end.getFullYear() !== start.getFullYear()
      ) {
        errs.ends = t('calendar.error.multiDayRequiresAllDay');
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [title, allDay, startsAt, endsAt, t]);

  // ── submit handler ──
  const handleSubmit = async () => {
    if (!validate()) return;

    // D-16: offline guard
    if (!navigator.onLine) {
      setErrors({ submit: t('calendar.error.offline') });
      return;
    }

    // Build reminders array
    const reminders: Array<{ member_id: string; lead_minutes: number }> = [];
    for (const preset of reminderPresets) {
      if (preset === 'none') continue;
      const lead = resolveLeadMinutes(preset, startsAt);
      for (const memberId of attendeeIds) {
        reminders.push({ member_id: memberId, lead_minutes: lead });
      }
    }

    // If no attendees selected yet (viewer still loading at open time),
    // fall back to the current viewer so the API min-1 constraint is satisfied.
    const resolvedAttendeeIds =
      attendeeIds.length > 0
        ? attendeeIds
        : viewer
          ? [viewer.id]
          : attendeeIds;

    const input: EventInput = {
      title: title.trim(),
      ...(description ? { description } : {}),
      starts_at: allDay ? `${startsAt.slice(0, 10)}T00:00:00.000Z` : new Date(startsAt).toISOString(),
      ends_at: (allDay || !endsAt) ? null : new Date(endsAt).toISOString(),
      all_day: allDay,
      ...(location ? { location } : {}),
      attendee_ids: resolvedAttendeeIds,
      ...(recurrence.type !== 'once' ? { recurrence } : {}),
      ...(reminders.length > 0 ? { reminders } : {}),
    };

    try {
      if (mode.kind === 'create') {
        await create.mutateAsync(input);
      } else {
        // API-et kjenner bare seriens id, ikke forekomst-id-en `serie|tid`.
        await update.mutateAsync({
          id: mode.seriesId,
          input,
          scope: isOneOff ? 'series' : scope,
          ...(occurrence ? { occurrence } : {}),
        });
      }
      onClose();
    } catch (err) {
      const error = err as Error;
      if (error.message.includes('5')) {
        setErrors({ submit: t('common.try_again') });
      } else {
        setErrors({ submit: t('calendar.error.saveFailed') });
      }
    }
  };

  // ── delete handler ──
  const handleDelete = async (variant: 'oneoff' | Scope) => {
    if (mode.kind !== 'edit') return;
    if (!navigator.onLine) {
      setErrors({ submit: t('calendar.error.offline') });
      return;
    }
    try {
      await remove.mutateAsync({
        id: mode.seriesId,
        scope: variant === 'oneoff' ? 'series' : variant,
        ...(variant !== 'oneoff' && occurrence ? { occurrence } : {}),
      });
      onClose();
    } catch {
      setErrors({ submit: t('calendar.error.saveFailed') });
    }
  };

  const isSubmitting = create.isPending || update.isPending || remove.isPending;

  // ── topphode: X · tittel · Lagre (D9) ──
  const header = (
    <SheetHeader
      titleId="event-edit-sheet-title"
      title={
        mode.kind === 'create'
          ? t('calendar.editSheet.titleCreate')
          : t('calendar.editSheet.titleEdit')
      }
      onClose={onClose}
      close={{ label: t('common.cancel'), disabled: isSubmitting, testId: 'cancel-btn' }}
      action={
        <Button
          size={44}
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
          data-testid="save-btn"
        >
          {t('common.save')}
        </Button>
      }
    />
  );

  // ── form content ──
  const formContent = (
    <div data-testid="event-edit-sheet" className="flex flex-col gap-4">
      {/* Hva skjer? */}
      <div className="flex flex-col">
        <SectionLabel as="label" htmlFor="event-title">
          {t('calendar.field.title')}
        </SectionLabel>
        <input
          id="event-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => {
            if (!title.trim()) setErrors((e) => ({ ...e, title: t('calendar.error.titleRequired') }));
          }}
          className="field text-heading-s font-bold"
          data-testid="event-title-input"
          aria-invalid={!!errors.title}
          aria-describedby={errors.title ? 'event-title-error' : undefined}
        />
        {errors.title && (
          <p id="event-title-error" className={FIELD_ERROR} role="alert">
            {errors.title}
          </p>
        )}
      </div>

      {/* Hele dagen — #152: Switch-primitiven, navngitt av etiketten. */}
      <div className="flex h-14 items-center justify-between rounded-md border border-card-line bg-card pl-4 pr-2">
        <span id="event-all-day-label" className="text-body font-bold text-ink">
          {t('calendar.field.allDay')}
        </span>
        <Switch
          checked={allDay}
          onChange={setAllDay}
          labelledBy="event-all-day-label"
          data-testid="all-day-toggle"
        />
      </div>

      {/* Start / Slutt */}
      {allDay ? (
        <div className="flex flex-col">
          <SectionLabel as="label" htmlFor="event-date">
            {t('calendar.field.date')}
          </SectionLabel>
          <input
            id="event-date"
            type="date"
            value={startsAt.slice(0, 10)}
            onChange={(e) => setStartsAt(e.target.value)}
            className="field"
            data-testid="event-date-input"
            lang="nb"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <SectionLabel as="label" htmlFor="event-starts">
              {t('calendar.field.starts')}
            </SectionLabel>
            <input
              id="event-starts"
              type="datetime-local"
              value={startsAt.slice(0, 16)}
              onChange={(e) => setStartsAt(e.target.value)}
              className="field"
              data-testid="event-starts-input"
              lang="nb"
            />
          </div>
          <div className="flex flex-col">
            <SectionLabel as="label" htmlFor="event-ends">
              {t('calendar.field.ends')}
            </SectionLabel>
            <input
              id="event-ends"
              type="datetime-local"
              value={endsAt.slice(0, 16)}
              onChange={(e) => setEndsAt(e.target.value)}
              className="field"
              data-testid="event-ends-input"
              aria-invalid={!!errors.ends}
              aria-describedby={errors.ends ? 'event-ends-error' : undefined}
              lang="nb"
            />
            {errors.ends && (
              <p id="event-ends-error" className={FIELD_ERROR} role="alert">
                {errors.ends}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Påminnelser */}
      <div className="flex flex-col">
        <SectionLabel as="span" id="event-reminder-label">
          {t('calendar.field.reminder')}
        </SectionLabel>
        <ReminderPresetChips
          value={reminderPresets}
          onChange={setReminderPresets}
          labelledBy="event-reminder-label"
        />
      </div>

      {/* Gjentas — raden har etiketten selv */}
      <RecurrenceBuilderField value={recurrence} onChange={setRecurrence} />

      {/* Override scope picker — only for recurring occurrence edits */}
      {isRecurringOccurrence && (
        <RecurrenceOverrideScopePicker value={scope} onChange={setScope} />
      )}

      {/* Created by caption — navnet fra medlemslista (#165). Skjules når skaperen
          ikke er kjent, så linja aldri står som en tom «Lagt til av». */}
      {mode.kind === 'edit' && mode.createdByName && (
        <p className="m-0 text-caption text-ink-2" data-testid="event-created-by">
          {t('calendar.editSheet.createdBy', { nick: mode.createdByName })}
        </p>
      )}

      {/* Submit error */}
      <SheetError message={errors.submit} data-testid="submit-error" />

      {/* Slett — gated by D-08 */}
      {canDelete && (
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isSubmitting}
          className="h-13 w-full rounded-pill border border-bad text-sm font-extrabold text-bad disabled:opacity-40"
          data-testid="delete-btn"
        >
          {t('calendar.action.delete')}
        </button>
      )}
    </div>
  );

  // ─── tablet centred modal ────────────────────────────────────────────────
  if (isTablet) {
    if (!open) return null;
    return (
      <>
        {/* Scrim */}
        <div
          className="fixed inset-0 z-50 bg-dim backdrop-blur-sm"
          onClick={onClose}
          data-testid="event-edit-scrim"
        />
        {/* Centred panel */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="event-edit-sheet-title"
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
        >
          <div
            className="pointer-events-auto w-130 max-h-vh-90 overflow-y-auto rounded-2xl border border-card-line bg-sheet px-6 pb-6 pt-3 text-ink shadow-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="event-edit-modal"
          >
            {header}
            {formContent}
          </div>
        </div>

        {/* Delete confirm sheet */}
        {mode.kind === 'edit' && (
          <DeleteEventConfirmSheet
            open={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={(variant) => void handleDelete(variant)}
            title={title}
            isRecurring={!isOneOff}
            scope={scope}
          />
        )}
      </>
    );
  }

  // ─── phone BottomSheet ───────────────────────────────────────────────────
  return (
    <>
      <BottomSheet
        open={open}
        onClose={onClose}
        header={header}
        labelledBy="event-edit-sheet-title"
        snapPoints="full"
      >
        {formContent}
      </BottomSheet>

      {/* Delete confirm sheet (renders above the edit sheet) */}
      {mode.kind === 'edit' && (
        <DeleteEventConfirmSheet
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={(variant) => void handleDelete(variant)}
          title={title}
          isRecurring={!isOneOff}
          scope={scope}
        />
      )}
    </>
  );
}
